import { UpgradeEntity } from '../types/UpgradeEntity';
import type { GameState } from '@/types';

export function defineUpgrades(): UpgradeEntity[] {
  return [
    new UpgradeEntity({
      id: 'automation',
      name: 'Basic Automation',
      cost: 50,
      description: 'Automates data collection.',
      upgradeStage: "data"
    }),

    new UpgradeEntity({
      id: 'dataCleaning',
      name: 'Data Cleaning',
      cost: 100,
      description: 'Enables data cleaning and clean data sales.',
      progressionRequirements: {
        upgrades: ['automation']
      },
      upgradeStage: "data"
    }),

    new UpgradeEntity({
      id: 'hr',
      name: 'HR',
      cost: 1000,
      description: 'Allows hiring of data scientists, engineers, and other personnel. Requires steady revenue stream.',
      progressionRequirements: {
        upgrades: ['automation'],
        infrastructure: { cleanDataApi: 1 }
      },
      upgradeStage: "data"
    }),

    new UpgradeEntity({
      id: 'dataStorage',
      name: 'Data Storage',
      cost: 200,
      description: 'Unlocks more storage infrastructure.',
      progressionRequirements: {
        upgrades: ["dataCleaning"]
      },
      upgradeStage: "data"
    }),

    new UpgradeEntity({
      id: 'streaming',
      name: 'Streaming Processing',
      cost: 1000,
      description: 'Unlocks streaming for real-time data processing.',
      progressionRequirements: {
        upgrades: ['automation', 'hr'],
        personnel: { dataEngineer: 1 }
      },
      upgradeStage: "data"
    }),

    new UpgradeEntity({
      id: 'ml',
      name: 'Machine Learning',
      cost: 3000,
      description: 'Unlocks ability to perform tasks without explicit instructions and relying on patterns',
      progressionRequirements: {
        upgrades: ['automation', 'dataMarketplace', 'hr'],
        personnel: { dataEngineer: 3 },
        infrastructure: { database: 1 }
      },
      upgradeStage: "data"
    }),

    new UpgradeEntity({
      id: 'dataMarketplace',
      name: 'Data Marketplace',
      cost: 500,
      description: 'Unlock APIs to automatically sell data and generate passive revenue. Requires automated data collection.',
      progressionRequirements: {
        infrastructure: { webScraper: 3, database: 1 }
      },
      upgradeStage: "data"
    }),
    
    new UpgradeEntity({
      id: 'cloud',
      name: 'Cloud Migration',
      cost: 1000,
      description: 'Moves to cloud: doubles capacity, boosts rates, halves failure, increases personnel efficiency.' ,
      effect: (state: GameState) => {
        state.productionRates.rawData = (state.productionRates.rawData || 0) + 100;
        state.productionRates.cleanData = (state.productionRates.cleanData || 0) + 50;
        // Double storage capacity via multipliers
        if (!state.storageCapacityMultipliers) state.storageCapacityMultipliers = { rawData: 1, cleanData: 1, feature: 1, model: 1, reasoningEngine: 1, researchProgress: 1 };
        state.storageCapacityMultipliers.rawData = (state.storageCapacityMultipliers.rawData || 1) * 2;
        state.storageCapacityMultipliers.cleanData = (state.storageCapacityMultipliers.cleanData || 1) * 2;
        state.storageCapacityMultipliers.model = (state.storageCapacityMultipliers.model || 1) * 2;
        state.storageCapacityMultipliers.feature = (state.storageCapacityMultipliers.feature || 1) * 2;
        // Reduce infrastructure failure rate by 70%
        state.failureRateMultiplier = (state.failureRateMultiplier || 1) * 0.3;
      },
      progressionRequirements: {
        personnel: { dataEngineer: 1 },
        infrastructure: { database: 1 }
      },
      upgradeStage: "data"
    }),

    new UpgradeEntity({
      id: 'redundancy',
      name: 'Increase Redundancy',
      cost: 500,
      description: 'Increase redundancy by implementing backup and recovery solutions, HA architectures and load balancing.' ,
      effect: (state: GameState) => {
        // // Reduce infrastructure failure rate by 90%
        state.failureRateMultiplier = (state.failureRateMultiplier || 1) * 0.1;
      },
      progressionRequirements: {
        personnel: { dataEngineer: 1 },
        infrastructure: { database: 1 }
      },
      upgradeStage: "data"
    }),

    new UpgradeEntity({
      id: 'dataCompression',
      name: 'Data Compression',
      cost: 500,
      description: 'Increases storage capacity.' ,
      effect: (state: GameState) => {
        if (!state.storageCapacityMultipliers) state.storageCapacityMultipliers = { rawData: 1, cleanData: 1, feature: 1, model: 1, reasoningEngine: 1, researchProgress: 1 };
        state.storageCapacityMultipliers.rawData = (state.storageCapacityMultipliers.rawData || 1) * 1.5;
        state.storageCapacityMultipliers.cleanData = (state.storageCapacityMultipliers.cleanData || 1) * 1.5;
      },
      progressionRequirements: {
        infrastructure: { database: 1 }
      },
      upgradeStage: "data"
    }),

    new UpgradeEntity({
      id: 'distributedStorage',
      name: 'Distributed Storage',
      cost: 1500,
      description: 'Doubles storage, increases expenses, reduces failure by 20% (needs 2 Data Engineers, 1000 raw data storage capacity).' ,
      effect: (state: GameState) => {
        if (!state.storageCapacityMultipliers) state.storageCapacityMultipliers = { rawData: 1, cleanData: 1, feature: 1, model: 1, reasoningEngine: 1, researchProgress: 1 };
        state.storageCapacityMultipliers.rawData = (state.storageCapacityMultipliers.rawData || 1) * 2;
        state.storageCapacityMultipliers.cleanData = (state.storageCapacityMultipliers.cleanData || 1) * 2;
        state.expenses.infrastructureExpensesPerSec = (state.expenses.infrastructureExpensesPerSec || 0) * 1.2;
        // Reduce infrastructure failure rate by 20% (distributed storage is more reliable)
        state.failureRateMultiplier = (state.failureRateMultiplier || 1) * 0.8;
      },
      progressionRequirements: {
        personnel: { dataEngineer: 2 },
        infrastructure: { database: 1 }
      },
      upgradeStage: "data"
    }),

    new UpgradeEntity({
      id: 'distributedComputing',
      name: 'Distributed Computing',
      cost: 1500,
      description: 'Boosts production, reduces failure by 30% (needs 2 Data Engineers).' ,
      effect: (state: GameState) => {
        state.productionRates.rawData = (state.productionRates.rawData || 0) * 1.5;
        state.productionRates.cleanData = (state.productionRates.cleanData || 0) * 1.5;
        // Reduce infrastructure failure rate by 30% (distributed systems are more reliable)
        state.failureRateMultiplier = (state.failureRateMultiplier || 1) * 0.7;
      },
      progressionRequirements: {
        upgrades: ['distributedStorage'],
        personnel: { dataEngineer: 2 },
        infrastructure: { database: 1 }
      },
      upgradeStage: "data"
    }),

    new UpgradeEntity({
      id: 'featureEngineering',
      name: 'Feature Storage',
      cost: 3000,
      description: 'Unlocks Feature Store infrastructure for centralized feature management and ML feature pipelines.',
      progressionRequirements: {
        upgrades: ['ml'],
        personnel: { mlEngineer: 1 }
      },
      upgradeStage: "data"
    }),

    new UpgradeEntity({
      id: 'modelTraining',
      name: 'Model Training',
      cost: 3000,
      description: 'Enables model training pipelines (needs ML Engineer).',
      progressionRequirements: {
        upgrades: ['ml'],
        personnel: { mlEngineer: 1 }
      },
      upgradeStage: "ml"
    }),

    new UpgradeEntity({
      id: 'advancedAI',
      name: 'Advanced AI',
      cost: 3000,
      description: 'Unlocks AI Researchers who conduct research and provide supervision for model training. Each AI Researcher provides 50 research points storage.',
      progressionRequirements: {
        upgrades: ['ml', 'modelTraining'],
        personnel: { mlEngineer: 1 }
      },
      upgradeStage: "ml"
    }),

    new UpgradeEntity({
      id: 'deepLearning',
      name: 'Deep Learning',
      cost: 5000,
      description: 'Neural networks with multiple layers. Foundational technique for modern AI. Improves model quality by 30%.',
      effect: (state: GameState) => {
        state.productionRates.model = (state.productionRates.model || 0) * 1.3;
      },
      progressionRequirements: {
        upgrades: ['ml', 'modelTraining', 'advancedAI'],
        personnel: { aiResearcher: 1 },
        resources: { researchProgress: 50 }
      },
      upgradeStage: "ml"
    }),

    new UpgradeEntity({
      id: 'attentionMechanism',
      name: 'Attention Mechanism',
      cost: 10000,
      description: 'Learn to focus on relevant parts of input data. Key innovation for sequence processing. Boosts research points by 20%.',
      effect: (state: GameState) => {
        state.productionRates.researchProgress = (state.productionRates.researchProgress || 0) * 1.2;
      },
      progressionRequirements: {
        upgrades: ['deepLearning', 'transformerArchitecture'],
        personnel: { aiResearcher: 2 },
        resources: { researchProgress: 100 }
      },
      upgradeStage: "ml"
    }),

    new UpgradeEntity({
      id: 'transformerArchitecture',
      name: 'Transformer Architecture',
      cost: 10000,
      description: 'Revolutionary architecture using self-attention. Enables modern large language models. Increases model production by 40%.',
      effect: (state: GameState) => {
        state.productionRates.model = (state.productionRates.model || 0) * 1.4;
      },
      progressionRequirements: {
        upgrades: ['deepLearning'],
        personnel: { aiResearcher: 3 },
        resources: { researchProgress: 100 }
      },
      upgradeStage: "ml"
    }),

    new UpgradeEntity({
      id: 'rlhf',
      name: 'RLHF',
      cost: 10000,
      description: 'Reinforcement Learning from Human Feedback. Improves model quality and alignment by 50%.',
      effect: (state: GameState) => {
        state.productionRates.model = (state.productionRates.model || 0) * 1.5;
      },
      progressionRequirements: {
        upgrades: ['deepLearning', 'agiSafety'],
        personnel: { aiResearcher: 1 },
        resources: { researchProgress: 100 }
      },
      upgradeStage: "ml"
    }),

    new UpgradeEntity({
      id: 'chainOfThought',
      name: 'Chain-of-Thought',
      cost: 15000,
      description: 'Enable models to break down complex problems step-by-step. Improves reasoning engine production by 50%.',
      effect: (state: GameState) => {
        state.productionRates.reasoningEngine = (state.productionRates.reasoningEngine || 0) * 1.5;
      },
      progressionRequirements: {
        upgrades: ['deepLearning'],
        personnel: { aiResearcher: 3 },
        resources: { researchProgress: 100 }
      },
      upgradeStage: "ml"
    }),

    new UpgradeEntity({
      id: 'multiModal',
      name: 'Multi-Modal Learning',
      cost: 20000,
      description: 'Process multiple data types simultaneously. Increases research points production by 50%.',
      effect: (state: GameState) => {
        state.productionRates.researchProgress = (state.productionRates.researchProgress || 0) * 1.5;
      },
      progressionRequirements: {
        upgrades: ['deepLearning', 'transformerArchitecture'],
        personnel: { aiResearcher: 4 },
        resources: { researchProgress: 200 }
      },
      upgradeStage: "ml"
    }),

    new UpgradeEntity({
      id: 'reasoningSystems',
      name: 'Reasoning Systems',
      cost: 15000,
      description: 'Unlocks Reasoning Specialists who fine-tune models for complex reasoning, and Inference Storage for managing reasoning engines.',
      progressionRequirements: {
        upgrades: ['chainOfThought'],
        personnel: { aiResearcher: 3 }
      },
      upgradeStage: "ml"
    }),

    new UpgradeEntity({
      id: 'agiSafety',
      name: 'AGI Safety Research',
      cost: 20000,
      description: 'Unlocks AGI Safety Researchers. Required before pursuing AGI development. Each AI Researcher provides 50 research points storage.',
      progressionRequirements: {
        upgrades: ['chainOfThought'],
        personnel: { aiResearcher: 3, reasoningSpecialist: 1 },
        resources: { researchProgress: 100 }
      },
      upgradeStage: "ml"
    }),

    new UpgradeEntity({
      id: 'agi',
      name: 'Artificial General Intelligence',
      cost: 100000,
      description: 'Begin AGI development. Requires safety researchers and extensive research.',
      progressionRequirements: {
        upgrades: ['multiModal', 'reasoningSystems', 'agiSafety'],
        personnel: { aiResearcher: 3, agiSafetyResearcher: 3, reasoningSpecialist: 2 },
        resources: { researchProgress: 400 }
      },
      upgradeStage: "ml"
    })
  ];
} 